import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useState } from 'react';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio/radio';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import { Select } from '@this/components/shared/ui/inputs/select';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@this/components/shared/ui/data_displays/table';
import { Modal, ModalBody, ModalHeader } from '@this/shared/ui/feedbacks/modal';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Heading } from '@this/shared/ui/data_displays/typography';
import { Switch } from '@this/components/shared/ui/inputs/switch/switch';
import { reportError } from '@this/lib/bugsnag';

const PROMPT_KIND_BIZ_EKINET = 'qr_nav_evidence_biz_ekinet';
const PROMPT_KIND_DOMESTIC_AIR_ANA = 'domestic_air_ana_evidence';
const PROMPT_KIND_DOMESTIC_AIR_JAL = 'domestic_air_jal_evidence';

const MAIN_TAB_QR_NAV = 'qr_nav_verification';
const MAIN_TAB_DOMESTIC_AIR_ANA = 'domestic_air_ana_verification';
const MAIN_TAB_DOMESTIC_AIR_JAL = 'domestic_air_jal_verification';

const MAIN_SETTINGS_TABS: { value: string; label: string }[] = [
  { value: MAIN_TAB_QR_NAV, label: '右ナビ自動照合（新幹線QR）' },
  { value: MAIN_TAB_DOMESTIC_AIR_ANA, label: '右ナビ自動照合（国内航空券ANA）' },
  { value: MAIN_TAB_DOMESTIC_AIR_JAL, label: '右ナビ自動照合（国内航空券JAL）' }
];

const DEFAULT_LLM_MODEL = 'gpt-5.4-mini';

const QR_NAV_VERIFICATION_PUT_PATH = '/arrangement/llm_system_prompt_versions/qr_nav_llm_verification_enabled';
const DOMESTIC_AIR_ANA_VERIFICATION_PUT_PATH =
  '/arrangement/llm_system_prompt_versions/domestic_air_ana_nav_llm_verification_enabled';
const DOMESTIC_AIR_JAL_VERIFICATION_PUT_PATH =
  '/arrangement/llm_system_prompt_versions/domestic_air_jal_nav_llm_verification_enabled';

interface IndexJson {
  kind: string;
  editable_body: string;
  selected_llm_model: string;
  latest_saved_version_id: number | null;
  model_options: { name: string; label: string }[];
  qr_nav_llm_verification_enabled: boolean;
  domestic_air_ana_nav_llm_verification_enabled: boolean;
  domestic_air_jal_nav_llm_verification_enabled: boolean;
  history: {
    id: number;
    created_at: string;
    arranger_name: string;
    body_preview: string;
    llm_model: string;
  }[];
}

interface ShowJson {
  id: number;
  kind: string;
  body: string;
  llm_model: string;
  created_at: string;
  arranger_name: string;
}

const PromptSettings: React.FC = () => {
  const [mainTab, setMainTab] = useState(MAIN_TAB_QR_NAV);
  const [body, setBody] = useState('');
  const [llmModel, setLlmModel] = useState(DEFAULT_LLM_MODEL);
  const [modelOptions, setModelOptions] = useState<{ name: string; label: string }[]>([]);
  const [qrNavVerificationEnabled, setQrNavVerificationEnabled] = useState(false);
  const [domesticAirAnaVerificationEnabled, setDomesticAirAnaVerificationEnabled] = useState(false);
  const [domesticAirJalVerificationEnabled, setDomesticAirJalVerificationEnabled] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [history, setHistory] = useState<IndexJson['history']>([]);
  const [detail, setDetail] = useState<ShowJson | null>(null);

  const promptKind = (() => {
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_ANA) return PROMPT_KIND_DOMESTIC_AIR_ANA;
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_JAL) return PROMPT_KIND_DOMESTIC_AIR_JAL;
    return PROMPT_KIND_BIZ_EKINET;
  })();

  const fetchIndex = useCallback(() => {
    setLoading(true);
    setSaveError(null);
    Fetcher.get<IndexJson>('/arrangement/llm_system_prompt_versions.json', { kind: promptKind })
      .then(res => {
        setBody(res.editable_body);
        setHistory(res.history);
        setModelOptions(res.model_options);
        setQrNavVerificationEnabled(res.qr_nav_llm_verification_enabled);
        setDomesticAirAnaVerificationEnabled(res.domestic_air_ana_nav_llm_verification_enabled);
        setDomesticAirJalVerificationEnabled(res.domestic_air_jal_nav_llm_verification_enabled);
        const selected = res.selected_llm_model;
        setLlmModel(selected || DEFAULT_LLM_MODEL);
      })
      .catch(() => {
        setSaveError('読み込みに失敗しました');
      })
      .finally(() => setLoading(false));
  }, [promptKind]);

  useEffect(() => {
    document.title = 'プロンプト設定';
    return () => {
      document.title = '管理画面';
    };
  }, []);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const persistQrNavEnabled = useCallback((next: boolean) => {
    setToggleBusy(true);
    setSaveError(null);
    Fetcher.put(QR_NAV_VERIFICATION_PUT_PATH, { qr_nav_llm_verification_enabled: next })
      .then(() => {
        setQrNavVerificationEnabled(next);
      })
      .catch(e => {
        setSaveError('有効／無効の更新に失敗しました');
        reportError(e);
      })
      .finally(() => setToggleBusy(false));
  }, []);

  const persistDomesticAirAnaEnabled = useCallback((next: boolean) => {
    setToggleBusy(true);
    setSaveError(null);
    Fetcher.put(DOMESTIC_AIR_ANA_VERIFICATION_PUT_PATH, {
      domestic_air_ana_nav_llm_verification_enabled: next
    })
      .then(() => {
        setDomesticAirAnaVerificationEnabled(next);
      })
      .catch(e => {
        setSaveError('有効／無効の更新に失敗しました');
        reportError(e);
      })
      .finally(() => setToggleBusy(false));
  }, []);

  const persistDomesticAirJalEnabled = useCallback((next: boolean) => {
    setToggleBusy(true);
    setSaveError(null);
    Fetcher.put(DOMESTIC_AIR_JAL_VERIFICATION_PUT_PATH, {
      domestic_air_jal_nav_llm_verification_enabled: next
    })
      .then(() => {
        setDomesticAirJalVerificationEnabled(next);
      })
      .catch(e => {
        setSaveError('有効／無効の更新に失敗しました');
        reportError(e);
      })
      .finally(() => setToggleBusy(false));
  }, []);

  const save = useCallback(() => {
    setSaving(true);
    setSaveError(null);
    Fetcher.post<{ id: number }>('/arrangement/llm_system_prompt_versions', {
      kind: promptKind,
      body,
      llm_model: llmModel
    })
      .then(() => fetchIndex())
      .catch(e => {
        setSaveError('保存に失敗しました');
        reportError(e);
      })
      .finally(() => setSaving(false));
  }, [body, fetchIndex, llmModel, promptKind]);

  const openDetail = useCallback(
    (id: number) => {
      Fetcher.get<ShowJson>(`/arrangement/llm_system_prompt_versions/${id}.json`, { kind: promptKind })
        .then(res => setDetail(res))
        .catch(e => {
          setSaveError('履歴の取得に失敗しました');
          reportError(e);
        });
    },
    [promptKind]
  );

  const verificationEnabled = (() => {
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_ANA) return domesticAirAnaVerificationEnabled;
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_JAL) return domesticAirJalVerificationEnabled;
    return qrNavVerificationEnabled;
  })();
  const persistVerificationEnabled = (() => {
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_ANA) return persistDomesticAirAnaEnabled;
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_JAL) return persistDomesticAirJalEnabled;
    return persistQrNavEnabled;
  })();
  const promptHeading = (() => {
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_ANA) return '証憑抽出：国内航空券ANA（Gmail）';
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_JAL) return '証憑抽出：国内航空券JAL（Gmail）';
    return '証憑抽出：びじえき（PDF）';
  })();
  const toggleLabel = (() => {
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_ANA) return '国内航空券ANA右ナビ自動照合';
    if (mainTab === MAIN_TAB_DOMESTIC_AIR_JAL) return '国内航空券JAL右ナビ自動照合';
    return '新幹線QR右ナビ自動照合';
  })();

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
      <Heading level="2">プロンプト設定</Heading>

      <RadioGroup layout="horizontal" value={mainTab} onChange={(_e, value) => setMainTab(value)}>
        {MAIN_SETTINGS_TABS.map(t => (
          <Radio key={t.value} value={t.value}>
            {t.label}
          </Radio>
        ))}
      </RadioGroup>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: 14 }}>{toggleLabel}</span>
        <Switch
          checked={verificationEnabled}
          onChange={next => {
            persistVerificationEnabled(next);
          }}
          disabled={toggleBusy}
          label={verificationEnabled ? '有効' : '無効'}
        />
      </div>

      <Heading level="3">{promptHeading}</Heading>

      {saveError && <div style={{ color: 'var(--color-danger, #c00)' }}>{saveError}</div>}

      {loading ? (
        <Loading />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontSize: 14 }}>モデル</span>
            <Select
              value={llmModel}
              onChange={e => setLlmModel(e.target.value)}
              disabled={saving}
              style={{ maxWidth: 480 }}
            >
              {modelOptions.map(option => (
                <option key={option.name} value={option.name}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <Textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            rowsMin={24}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button color="primary" onClick={() => save()} disabled={saving}>
              保存（新しいバージョンとして追記）
            </Button>
          </div>

          <Heading level="3">履歴</Heading>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell nowrap>ID</TableCell>
                <TableCell nowrap>日時</TableCell>
                <TableCell nowrap>変更者</TableCell>
                <TableCell nowrap>モデル</TableCell>
                <TableCell>概要</TableCell>
                <TableCell nowrap>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(h => (
                <TableRow key={h.id}>
                  <TableCell nowrap>{h.id}</TableCell>
                  <TableCell nowrap>{h.created_at}</TableCell>
                  <TableCell nowrap>{h.arranger_name}</TableCell>
                  <TableCell nowrap>{h.llm_model}</TableCell>
                  <TableCell style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {h.body_preview}
                  </TableCell>
                  <TableCell nowrap>
                    <Button color="sub" onClick={() => openDetail(h.id)}>
                      全文
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {detail !== null && (
        <Modal open size="large" onClose={() => setDetail(null)}>
          <ModalHeader>{`履歴 #${detail.id}`}</ModalHeader>
          <ModalBody>
            <div style={{ marginBottom: 8 }}>
              {detail.created_at} / {detail.arranger_name} / {detail.llm_model}
            </div>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '70vh',
                overflow: 'auto',
                fontSize: 13
              }}
            >
              {detail.body}
            </pre>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default PromptSettings;
